// hooks/useTournamentMetadata.ts
//
// Shared accessor for the /tournamentMetadata collection. The
// boilerplate replaced here showed up in 20+ files: fetch the
// metadata, get the live ID set, filter orphaned metadata, then
// status-filter. Each one wrote the same pipeline from scratch.
//
// Why orphan filtering? Closing a tournament removes it from
// /tournaments but the cleanup of /tournamentMetadata is best-
// effort — stale rows do show up. Cross-checking against the live
// ID set is the simplest way to keep dashboards from listing
// archived ghosts. We read that ID set via the REST shallow=true
// endpoint (keys only, a few KB) — NOT a full /tournaments get(),
// which would transfer the entire multi-MB engine/rounds/players
// tree just to recover its keys. See fetchLiveTournamentIds.
//
// One-shot vs subscribe:
//   - one-shot (default): use for views that load once per mount.
//     Most dashboards, the staff settings page, the appeals log.
//   - subscribe: use when the view needs to react in real time to
//     a new tournament appearing or a status/round/player change
//     mid-session. Costs an onValue listener on /tournamentMetadata
//     (small); the orphan-filter id set is still a one-shot shallow
//     read since /tournaments turning over rarely matters here.

import { useEffect, useState, useCallback } from 'react';
import { ref, get, onValue } from 'firebase/database';
import { db } from '../config/firebase';
import { fetchLiveTournamentIds } from '../utils/liveTournamentIds';

export interface TournamentMetadataEntry {
  id: string;
  name?: string;
  status?: string;
  currentRound?: number;
  playerCount?: number;
  format?: string;
  engineStatus?: string;
  isPublic?: boolean;
  staffAssignments?: Record<string, boolean>;
  allowedRoles?: string[];
  eventDate?: number;
  eventEndDate?: number;
  ownerUid?: string;
  ownerStoreId?: string;
  createdByUid?: string;
  createdAt?: number;
  updatedAt?: number;
  floorTeams?: Record<string, unknown>;
  /** Catch-all so callers reading less-common fields don't need
   *  to extend this type. They get `unknown` and cast at the use
   *  site. */
  [key: string]: unknown;
}

export interface UseTournamentMetadataOptions {
  /** Status filter. Pass `null` for no filter (return everything,
   *  including archived). Default: 'active'. */
  status?: 'active' | 'archived' | null;
  /** Skip the orphan filter — don't cross-check against
   *  /tournaments. Useful when the consumer wants to see archived
   *  rows or when the /tournaments read would be denied. Default:
   *  false. */
  includeOrphaned?: boolean;
  /** Use onValue() instead of get(). When the consumer needs to
   *  react to a new tournament landing mid-session. Default:
   *  false. */
  subscribe?: boolean;
}

export interface UseTournamentMetadataResult {
  tournaments: TournamentMetadataEntry[];
  loading: boolean;
  error: Error | null;
  /** Re-fetch on demand. No-op in subscribe mode (the listener
   *  already keeps state fresh). */
  refetch: () => void;
}

const DEFAULT_OPTIONS: Required<UseTournamentMetadataOptions> = {
  status: 'active',
  includeOrphaned: false,
  subscribe: false,
};

function toEntries(
  metadata: Record<string, Record<string, unknown>> | null,
  activeIds: Set<string> | null,
  statusFilter: 'active' | 'archived' | null,
): TournamentMetadataEntry[] {
  if (!metadata) return [];
  return Object.entries(metadata)
    .filter(([id, t]) => {
      if (activeIds && !activeIds.has(id)) return false;
      if (statusFilter && (t as { status?: string }).status !== statusFilter) {
        return false;
      }
      return true;
    })
    .map(([id, t]) => ({ id, ...t }) as TournamentMetadataEntry);
}

export function useTournamentMetadata(
  options?: UseTournamentMetadataOptions,
): UseTournamentMetadataResult {
  const status = options?.status ?? DEFAULT_OPTIONS.status;
  const includeOrphaned = options?.includeOrphaned ?? DEFAULT_OPTIONS.includeOrphaned;
  const subscribe = options?.subscribe ?? DEFAULT_OPTIONS.subscribe;

  const [tournaments, setTournaments] = useState<TournamentMetadataEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Bumping nonce forces the effect to re-run for an on-demand
  // refetch — simpler than threading an imperative AbortController.
  const [refetchNonce, setRefetchNonce] = useState(0);

  const refetch = useCallback(() => setRefetchNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const apply = (
      metadata: Record<string, Record<string, unknown>> | null,
      activeIds: Set<string> | null,
    ) => {
      if (cancelled) return;
      setTournaments(toEntries(metadata, activeIds, status));
      setLoading(false);
    };

    const fail = (err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    };

    if (subscribe) {
      // metadata stays live via onValue; the orphan ID set is a one-shot
      // shallow read (keys only, a few KB — never the /tournaments tree).
      let activeIds: Set<string> | null = null;
      let latestMeta: Record<string, Record<string, unknown>> | null = null;

      if (!includeOrphaned) {
        fetchLiveTournamentIds().then((ids) => {
          if (cancelled) return;
          activeIds = ids;
          // Re-filter the most recent snapshot once the id set lands.
          if (latestMeta !== null) apply(latestMeta, activeIds);
        });
      }

      const unsub = onValue(
        ref(db, 'tournamentMetadata'),
        (snap) => {
          latestMeta = snap.val() as Record<string, Record<string, unknown>> | null;
          apply(latestMeta, activeIds);
        },
        fail,
      );
      return () => {
        cancelled = true;
        unsub();
      };
    }

    // One-shot mode: parallel fetch of metadata + the shallow live-id set.
    Promise.all([
      get(ref(db, 'tournamentMetadata')),
      includeOrphaned ? Promise.resolve(null) : fetchLiveTournamentIds(),
    ])
      .then(([metaSnap, activeIds]) => {
        const metadata = metaSnap.val() as Record<string, Record<string, unknown>> | null;
        apply(metadata, activeIds);
      })
      .catch(fail);

    return () => {
      cancelled = true;
    };
  }, [status, includeOrphaned, subscribe, refetchNonce]);

  return { tournaments, loading, error, refetch };
}

export default useTournamentMetadata;

export interface FetchTournamentMetadataOnceResult {
  tournaments: TournamentMetadataEntry[];
  /** Live /tournaments ID set used for the orphan check. `null`
   *  when includeOrphaned was true (no /tournaments read was
   *  done). Exposed so a caller can reuse it for related
   *  bookkeeping — e.g. cleaning up stale tournamentAssignments
   *  on the user record — without paying for a second read of
   *  the (large) /tournaments subtree. */
  activeIds: Set<string> | null;
}

// Imperative one-shot variant of the hook. Same orphan +
// status filter; for use inside async callbacks (e.g. a
// "publish" button handler) where pulling state through a hook
// isn't worth it. Same defaults as useTournamentMetadata.
export async function fetchTournamentMetadataOnce(
  options?: UseTournamentMetadataOptions,
): Promise<FetchTournamentMetadataOnceResult> {
  const status = options?.status ?? DEFAULT_OPTIONS.status;
  const includeOrphaned = options?.includeOrphaned ?? DEFAULT_OPTIONS.includeOrphaned;

  const [metaSnap, activeIds] = await Promise.all([
    get(ref(db, 'tournamentMetadata')),
    includeOrphaned ? Promise.resolve(null) : fetchLiveTournamentIds(),
  ]);

  const metadata = metaSnap.val() as Record<string, Record<string, unknown>> | null;
  return { tournaments: toEntries(metadata, activeIds, status), activeIds };
}
